import { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useStable } from "../context/StableContext";
import api, { errText } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { ViewTabs } from "../components/ViewTabs";
import { RationGroupsEditor } from "../components/RationGroupsEditor";
import { FeedingBulkDialog } from "../components/FeedingBulkDialog";
import { SlotBlocks } from "../components/ProfendaBlocks";
import { Loader2, Search, ArrowLeft, Plus, Pencil, Wrench, History, ChevronRight, CheckSquare, Check, Trash2, AlertTriangle, UserCog } from "lucide-react";
import { toast } from "sonner";
import { fmtDateIT, fmtQty, todayISO, groupRationBySlot, usedCategoriesInOrder, rationToGroups, groupsToPayload, slotState, coverageMissing, hasProfenda, slotOwnerManaged, isFullyOwnerManaged, SLOT_STATE, DEFAULT_CATEGORY_ORDER } from "../lib/feedingConstants";

const VIEW_KEY = "equiro_feeding_view";

// Profenda content honoring the shared fascia filter. `all` = organized by fascia; single fascia =
// only that slot's foods (no repeated fascia name). Distinguishes no-ration vs empty-in-slot.
function ProfendaContent({ ration, fascia, activeSlots, itemsMap, catOrder, hid, variant, setting }) {
  const Empty = ({ tid, txt }) => variant === "inline"
    ? <span className="text-xs text-[#9AA39C] italic" data-testid={tid}>{txt}</span>
    : <p className="text-sm text-[#9AA39C] italic" data-testid={tid}>{txt}</p>;
  const Warn = () => <span data-testid={`feeding-horse-nonconfig-${hid}`} className="inline-flex items-center gap-1 text-[11px] font-medium text-[#B4791F]"><AlertTriangle className="h-3 w-3" /> Non configurata</span>;
  const NoRation = () => <span className="text-xs text-[#9AA39C] italic">Nessuna razione prevista</span>;
  const Neutral = () => <span className="text-xs text-[#C9BDA0]">—</span>;
  const Owner = () => <span data-testid={`feeding-horse-owner-${hid}`} className="text-xs text-[#8C6D4F] italic">Gestita dal proprietario</span>;
  const slotLinesFor = (sid) => (ration.lines || []).filter((l) => l.meal_slot_id === sid);

  // Ghost current ration (no lines & no empty_slots) is derived as "no profenda" — never 3× ⚠.
  if (!hasProfenda(ration)) return <Empty tid={`feeding-horse-noration-${hid}`} txt="Nessuna profenda configurata" />;

  if (fascia !== "all") {
    if (slotOwnerManaged(setting, fascia)) return <Owner />;
    const slot = activeSlots.find((s) => s.id === fascia);
    const required = slot?.coverage_required !== false;
    const st = slotState(ration, fascia);
    if (st === SLOT_STATE.CONFIGURED) return <SlotBlocks slotLines={slotLinesFor(fascia)} itemsMap={itemsMap} catOrder={catOrder} variant={variant} />;
    if (st === SLOT_STATE.NONE) return required ? <Warn /> : <Neutral />;
    return <NoRation />;
  }

  const rows = activeSlots.map((s) => ({ s, st: slotState(ration, s.id), required: s.coverage_required !== false, owner: slotOwnerManaged(setting, s.id) }));
  const body = (st, sid, required, owner) => owner
    ? <Owner />
    : st === SLOT_STATE.CONFIGURED
      ? <SlotBlocks slotLines={slotLinesFor(sid)} itemsMap={itemsMap} catOrder={catOrder} variant={variant} />
      : st === SLOT_STATE.EMPTY ? <NoRation /> : (required ? <Warn /> : <Neutral />);
  if (variant === "inline") {
    return <div className="flex flex-wrap gap-x-6 gap-y-1.5">{rows.map(({ s, st, required, owner }) => (<div key={s.id} className="min-w-0"><span className="font-mono-label text-[10px] uppercase text-[#8C532B] mr-1.5">{s.name}</span>{body(st, s.id, required, owner)}</div>))}</div>;
  }
  return <div className="space-y-2">{rows.map(({ s, st, required, owner }) => (<div key={s.id}><span className="font-mono-label text-[10px] uppercase text-[#8C532B]">{s.name}</span><div className="mt-0.5">{body(st, s.id, required, owner)}</div></div>))}</div>;
}

const CheckBox = ({ on }) => (
  <span className={`shrink-0 h-5 w-5 rounded border flex items-center justify-center ${on ? "bg-[#1B3B2B] border-[#1B3B2B]" : "bg-white border-[#C9BDA0]"}`}>{on && <Check className="h-3.5 w-3.5 text-white" />}</span>
);

export default function FeedingHorses({ initialHorse }) {
  const { current, perms } = useStable();
  const canManage = perms.canManage;
  const [params, setParams] = useSearchParams();
  const selected = params.get("horse") || initialHorse || null;

  const [horses, setHorses] = useState(null);
  const [items, setItems] = useState([]);
  const [slots, setSlots] = useState([]);
  const [rations, setRations] = useState([]);
  const [settings, setSettings] = useState([]);
  const [catOrder, setCatOrder] = useState(DEFAULT_CATEGORY_ORDER);
  const [q, setQ] = useState("");
  const [fascia, setFascia] = useState("all");          // shared visual filter (Card + Elenco), not persisted
  const [view, setView] = useState(() => { try { return localStorage.getItem(VIEW_KEY) || "card"; } catch { return "card"; } });
  const setViewP = (v) => { setView(v); try { localStorage.setItem(VIEW_KEY, v); } catch { /* noop */ } };

  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [coverageOpen, setCoverageOpen] = useState(false);

  const itemsMap = useMemo(() => Object.fromEntries(items.map((i) => [i.id, i])), [items]);
  const activeSlots = useMemo(() => slots.filter((s) => !s.archived), [slots]);
  const activeItems = useMemo(() => items.filter((i) => !i.archived), [items]);
  const rationByHorse = useMemo(() => Object.fromEntries(rations.map((r) => [r.horse_id, r])), [rations]);
  const settingByHorse = useMemo(() => Object.fromEntries(settings.map((s) => [s.horse_id, s])), [settings]);

  const loadAll = useCallback(async (silent = false) => {
    if (!current) return; if (!silent) setHorses(null);
    try {
      const [h, it, sl, rt, co, ms] = await Promise.all([
        api.get(`/stables/${current.id}/horses`),
        api.get(`/stables/${current.id}/feed-items`, { params: { include_archived: true } }),
        api.get(`/stables/${current.id}/feed-meal-slots`),
        api.get(`/stables/${current.id}/feed-rations/current`),
        api.get(`/stables/${current.id}/feed-category-order`),
        api.get(`/stables/${current.id}/feed-horse-settings`),
      ]);
      setHorses(h.data); setItems(it.data); setSlots(sl.data); setRations(rt.data); setCatOrder(co.data.order); setSettings(ms.data);
    } catch (e) { setHorses([]); toast.error(errText(e.response?.data?.detail)); }
  }, [current]);
  useEffect(() => { loadAll(); }, [loadAll]);
  // Keep the fascia filter valid if slots change (e.g. selected slot archived).
  useEffect(() => { if (fascia !== "all" && !activeSlots.some((s) => s.id === fascia)) setFascia("all"); }, [activeSlots, fascia]);

  const openHorse = (hid) => { const p = new URLSearchParams(params); p.set("horse", hid); setParams(p); };
  const closeHorse = () => { const p = new URLSearchParams(params); p.delete("horse"); setParams(p); };

  if (selected) {
    return <ProfendaDetail stableId={current.id} horseId={selected} horse={(horses || []).find((h) => h.id === selected)}
      canManage={canManage} itemsMap={itemsMap} activeSlots={activeSlots} activeItems={activeItems} catOrder={catOrder}
      onBack={closeHorse} onChanged={() => loadAll(true)} />;
  }
  if (!horses) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#1B3B2B]" /></div>;

  const searched = horses.filter((h) => !q.trim() || (h.display_name || "").toLowerCase().includes(q.trim().toLowerCase()));
  // Main list shows ONLY profende with something the STABLE actually manages. A slot counts for
  // visibility if it is stable-managed for this horse AND configured. This hides: no-profenda,
  // empty ghost, no configured food, and fully owner-managed horses. Ambra (food + a required gap)
  // stays visible. Coverage stays independent: `incomplete` is derived from the full horses[].
  const stableConfiguredInSlot = (h, sid) => !slotOwnerManaged(settingByHorse[h.id], sid) && slotState(rationByHorse[h.id], sid) === SLOT_STATE.CONFIGURED;
  const visible = fascia === "all"
    ? searched.filter((h) => activeSlots.some((s) => stableConfiguredInSlot(h, s.id)))
    : searched.filter((h) => stableConfiguredInSlot(h, fascia));
  const incomplete = horses.map((h) => ({ h, missing: coverageMissing(rationByHorse[h.id], activeSlots, settingByHorse[h.id]) })).filter((x) => x.missing.length > 0);

  const enterSelect = () => { setSelectMode(true); setSelectedIds(new Set()); };
  const exitSelect = () => { setSelectMode(false); setSelectedIds(new Set()); };
  const toggle = (hid) => setSelectedIds((s) => { const n = new Set(s); n.has(hid) ? n.delete(hid) : n.add(hid); return n; });
  const selectAllVisible = () => setSelectedIds(new Set(visible.map((h) => h.id)));
  const deselectAll = () => setSelectedIds(new Set());
  const selectedHorses = horses.filter((h) => selectedIds.has(h.id));
  const handleClick = (hid) => { if (selectMode) toggle(hid); else openHorse(hid); };

  const contentProps = { fascia, activeSlots, itemsMap, catOrder };

  return (
    <div className="space-y-4" data-testid="feeding-horses">
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <div className="relative flex-1 min-w-0 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8C532B]" />
          <input data-testid="feeding-horse-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca cavallo…" className="w-full pl-9 pr-3 py-2 rounded-md bg-white border border-[#E5DEC9] text-base sm:text-sm outline-none placeholder:text-[#9aa39c]" />
        </div>
        <div className="flex items-center gap-2 flex-wrap sm:ml-auto">
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] font-mono-label uppercase text-[#8C532B] hidden sm:inline">Fascia</span>
            <Select value={fascia} onValueChange={setFascia}>
              <SelectTrigger className="h-9 bg-white w-[150px]" data-testid="feeding-fascia-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="fascia-opt-all">Tutte le fasce</SelectItem>
                {activeSlots.map((s) => <SelectItem key={s.id} value={s.id} data-testid={`fascia-opt-${s.id}`}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {canManage && <Button variant="outline" size="sm" data-testid="bulk-open-btn" onClick={() => setBulkOpen(true)}><CheckSquare className="h-4 w-4 mr-1.5" /> Azione multipla</Button>}
          <ViewTabs value={view} onChange={setViewP} options={[{ value: "card", label: "Card", testid: "feeding-view-card" }, { value: "list", label: "Elenco", testid: "feeding-view-list" }]} />
        </div>
      </div>

      {incomplete.length > 0 && (
        <button type="button" data-testid="coverage-alert" onClick={() => setCoverageOpen(true)} className="w-full flex items-center gap-2 rounded-lg border border-[#E7C976] bg-[#FBF4DE] px-3 py-2 text-left hover:bg-[#F8EFD2] transition-colors">
          <AlertTriangle className="h-4 w-4 text-[#B4791F] shrink-0" />
          <span className="text-sm text-[#7A5610]"><span className="font-semibold" data-testid="coverage-alert-count">{incomplete.length}</span> cavall{incomplete.length === 1 ? "o" : "i"} con Profenda incompleta</span>
          <ChevronRight className="h-4 w-4 text-[#B4791F] ml-auto shrink-0" />
        </button>
      )}
      <Dialog open={coverageOpen} onOpenChange={setCoverageOpen}>
        <DialogContent className="max-h-[80vh] overflow-y-auto" data-testid="coverage-dialog">
          <DialogHeader><DialogTitle className="font-display">Profende incomplete</DialogTitle></DialogHeader>
          <p className="text-xs text-[#8C532B] -mt-1">Fasce non ancora configurate (né razione, né dichiarate senza razione).</p>
          <div className="space-y-1.5">
            {incomplete.map(({ h, missing }) => (
              <button key={h.id} type="button" data-testid={`coverage-row-${h.id}`} onClick={() => { setCoverageOpen(false); openHorse(h.id); }} className="w-full flex items-center justify-between gap-3 rounded-lg border border-[#F0EAE1] bg-white px-3 py-2 text-left hover:border-[#8C532B]">
                <span className="font-semibold text-[#1B3B2B] truncate">{h.display_name}</span>
                <span className="text-xs text-[#B4791F] text-right shrink-0">{missing.join(", ")}</span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {selectMode && false && (
        <div className="hidden" data-testid="bulk-toolbar" />
      )}

      {visible.length === 0 ? <div className="bg-white rounded-xl border border-[#E5DEC9] py-16 text-center text-[#5F6B64]" data-testid="feeding-horses-empty">Nessun cavallo.</div>
        : view === "card" ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {visible.map((h) => {
              const on = selectedIds.has(h.id);
              return (
                <button key={h.id} data-testid={`feeding-horse-${h.id}`} onClick={() => openHorse(h.id)} className="text-left rounded-xl border bg-white p-4 transition-colors flex flex-col gap-2 border-[#E5DEC9] hover:border-[#8C532B]">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <p className="font-semibold text-[#1B3B2B] truncate">{h.display_name}</p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-[#8C532B] shrink-0" />
                  </div>
                  <ProfendaContent ration={rationByHorse[h.id]} hid={h.id} variant="card" setting={settingByHorse[h.id]} {...contentProps} />
                </button>
              );
            })}
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-[#E5DEC9] divide-y divide-[#F0EAE1]" data-testid="feeding-horse-list">
            {visible.map((h) => {
              const on = selectedIds.has(h.id);
              return (
                <button key={h.id} data-testid={`feeding-horse-${h.id}`} onClick={() => openHorse(h.id)} className="w-full text-left px-4 py-3 flex items-start gap-3 transition-colors hover:bg-[#FBF8F2]">
                  <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:gap-3">
                    <span className="font-semibold text-[#1B3B2B] sm:w-44 shrink-0 truncate">{h.display_name}</span>
                    <div className="flex-1 min-w-0 mt-0.5 sm:mt-0"><ProfendaContent ration={rationByHorse[h.id]} hid={h.id} variant="inline" setting={settingByHorse[h.id]} {...contentProps} /></div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-[#8C532B] shrink-0 mt-0.5" />
                </button>
              );
            })}
          </div>
        )}

      {bulkOpen && (
        <FeedingBulkDialog open={bulkOpen} stableId={current.id} horses={horses} settingByHorse={settingByHorse}
          activeSlots={activeSlots} activeItems={activeItems} itemsMap={itemsMap} catOrder={catOrder}
          onClose={() => setBulkOpen(false)} onApplied={() => { loadAll(true); }} />
      )}
    </div>
  );
}

function ProfendaDetail({ stableId, horseId, horse, canManage, itemsMap, activeSlots, activeItems, catOrder, onBack, onChanged }) {
  const [ration, setRation] = useState(undefined);
  const [editor, setEditor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [histOpen, setHistOpen] = useState(false);
  const [history, setHistory] = useState(null);
  const [delOpen, setDelOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [mgmt, setMgmt] = useState(null);
  const [mgmtEditor, setMgmtEditor] = useState(null);
  const [mgmtSaving, setMgmtSaving] = useState(false);

  const load = useCallback(() => {
    setRation(undefined);
    api.get(`/stables/${stableId}/horses/${horseId}/feed-ration`).then((r) => setRation(r.data || null)).catch((e) => { setRation(null); toast.error(errText(e.response?.data?.detail)); });
    api.get(`/stables/${stableId}/horses/${horseId}/feed-management`).then((r) => setMgmt(r.data)).catch(() => setMgmt({ managed_by: "scuderia", scope: "all", owner_slots: [], note: null }));
  }, [stableId, horseId]);
  useEffect(() => { load(); }, [load]);

  const slotName = (id) => activeSlots.find((s) => s.id === id)?.name || "—";
  const mgmtStatusText = (m) => {
    if (!m || m.managed_by !== "proprietario") return "Gestita dalla scuderia";
    if (m.scope === "all") return "Gestita dal proprietario · tutte le fasce";
    const names = (m.owner_slots || []).map(slotName);
    return names.length ? `Gestita dal proprietario · ${names.join(", ")}` : "Gestita dalla scuderia";
  };
  const openMgmt = () => setMgmtEditor({ managed_by: mgmt?.managed_by || "scuderia", scope: mgmt?.scope || "all", owner_slots: [...(mgmt?.owner_slots || [])], note: mgmt?.note || "" });
  const toggleOwnerSlot = (sid) => setMgmtEditor((m) => ({ ...m, owner_slots: m.owner_slots.includes(sid) ? m.owner_slots.filter((x) => x !== sid) : [...m.owner_slots, sid] }));
  const saveMgmt = async () => {
    setMgmtSaving(true);
    try {
      const payload = { managed_by: mgmtEditor.managed_by, scope: mgmtEditor.scope,
        owner_slots: (mgmtEditor.managed_by === "proprietario" && mgmtEditor.scope === "some") ? mgmtEditor.owner_slots : [],
        note: mgmtEditor.note || null };
      const r = await api.put(`/stables/${stableId}/horses/${horseId}/feed-management`, payload);
      setMgmt(r.data); setMgmtEditor(null); toast.success("Gestione alimentazione aggiornata"); onChanged && onChanged();
    } catch (err) { toast.error(errText(err.response?.data?.detail)); } finally { setMgmtSaving(false); }
  };

  const openEditor = (mode) => {
    const has = ration && ((ration.lines || []).length || (ration.empty_slots || []).length);
    const groups = (has && (mode === "correct" || mode === "new")) ? rationToGroups(ration, activeSlots) : [];
    setEditor({ mode, valid_from: mode === "correct" ? (ration?.valid_from || todayISO()) : todayISO(), note: (mode === "correct" ? ration?.note : "") || "", groups });
  };

  const save = async () => {
    const { lines, empty_slots, error } = groupsToPayload(editor.groups);
    if (error) return toast.error(error);
    setSaving(true);
    try {
      if (editor.mode === "correct") await api.put(`/stables/${stableId}/horses/${horseId}/feed-ration/correct`, { valid_from: editor.valid_from, lines, empty_slots, note: editor.note || null });
      else await api.post(`/stables/${stableId}/horses/${horseId}/feed-ration`, { valid_from: editor.valid_from, lines, empty_slots, note: editor.note || null });
      toast.success(editor.mode === "correct" ? "Profenda corretta" : "Profenda aggiornata (nuova versione)");
      setEditor(null); load(); onChanged && onChanged();
    } catch (err) { toast.error(errText(err.response?.data?.detail)); } finally { setSaving(false); }
  };

  const doDelete = async () => {
    setDeleting(true);
    try {
      const r = await api.delete(`/stables/${stableId}/horses/${horseId}/feed-ration`);
      toast.success(r.data?.had_history ? "Profenda eliminata. Le versioni precedenti restano nello storico." : "Profenda eliminata.");
      setDelOpen(false); load(); onChanged && onChanged();
    } catch (err) { toast.error(errText(err.response?.data?.detail)); } finally { setDeleting(false); }
  };

  const openHistory = () => { setHistOpen(true); setHistory(null); api.get(`/stables/${stableId}/horses/${horseId}/feed-ration/history`).then((r) => setHistory(r.data)).catch((e) => { setHistory([]); toast.error(errText(e.response?.data?.detail)); }); };

  const groups = groupRationBySlot(ration?.lines, activeSlots, itemsMap, catOrder);
  const usedCats = usedCategoriesInOrder(ration?.lines, itemsMap, catOrder);
  const cellText = (slotLines, cat) => {
    const ls = slotLines.filter((l) => itemsMap[l.feed_item_id]?.category === cat);
    if (!ls.length) return null;
    return ls.map((l) => `${itemsMap[l.feed_item_id]?.name || "?"} · ${l.quantity === null || l.quantity === undefined ? "nessun dato" : fmtQty(l.quantity, itemsMap[l.feed_item_id]?.base_unit)}`).join(" / ");
  };

  return (
    <div className="space-y-5" data-testid="profenda-detail">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-[#5F6B64]" data-testid="profenda-back"><ArrowLeft className="h-4 w-4" /> Cavalli</button>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-bold text-[#1B3B2B]" data-testid="profenda-horse-name">{horse?.display_name || "Cavallo"}</h2>
          {ration && <p className="text-xs text-[#8C532B]" data-testid="profenda-valid-from">Valida dal {fmtDateIT(ration.valid_from)}</p>}
        </div>
        <div className="flex gap-2 flex-wrap justify-end">
          <Button variant="ghost" size="sm" data-testid="profenda-history-btn" onClick={openHistory}><History className="h-4 w-4 mr-1.5" /> Storico</Button>
          {canManage && ration && <Button variant="outline" size="sm" data-testid="profenda-correct-btn" onClick={() => openEditor("correct")}><Wrench className="h-4 w-4 mr-1.5" /> Correggi</Button>}
          {canManage && <Button size="sm" data-testid="profenda-edit-btn" onClick={() => openEditor("new")} className="bg-[#1B3B2B] hover:bg-[#142E1F]"><Pencil className="h-4 w-4 mr-1.5" /> {ration ? "Modifica profenda" : "Configura profenda"}</Button>}
          {canManage && ration && <Button variant="ghost" size="sm" data-testid="profenda-delete-btn" onClick={() => setDelOpen(true)} className="text-red-600 hover:text-red-700 hover:bg-red-50"><Trash2 className="h-4 w-4 mr-1.5" /> Elimina</Button>}
        </div>
      </div>

      {mgmt && (
        <div data-testid="feed-mgmt-card" className={`rounded-xl border px-4 py-3 flex items-center justify-between gap-3 ${mgmt.managed_by === "proprietario" ? "border-[#DED6C2] bg-[#FBF6EE]" : "border-[#E5DEC9] bg-[#FCFAF6]"}`}>
          <div className="min-w-0">
            <span className="font-mono-label text-[10px] uppercase text-[#8C532B]">Gestione alimentazione</span>
            <p className="text-sm text-[#1B3B2B]" data-testid="feed-mgmt-status">{mgmtStatusText(mgmt)}</p>
            {mgmt.note && <p className="text-xs text-[#8C6D4F] italic mt-0.5" data-testid="feed-mgmt-note-display">{mgmt.note}</p>}
          </div>
          {canManage && <Button variant="outline" size="sm" data-testid="feed-mgmt-edit-btn" onClick={openMgmt} className="shrink-0"><UserCog className="h-4 w-4 mr-1.5" /> Modifica</Button>}
        </div>
      )}

      <Dialog open={!!mgmtEditor} onOpenChange={(o) => !o && setMgmtEditor(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="feed-mgmt-dialog">
          <DialogHeader><DialogTitle className="font-display">Gestione alimentazione</DialogTitle></DialogHeader>
          {mgmtEditor && (
            <div className="space-y-4">
              <p className="text-xs text-[#8C532B] bg-[#F7F3EC] rounded-lg p-2.5">Indica chi gestisce operativamente l'alimentazione di questo cavallo. «Gestita dal proprietario» esclude dal controllo della scuderia solo le fasce indicate; non modifica la profenda né lo storico.</p>
              <div className="space-y-1.5">
                {[["scuderia", "Gestita dalla scuderia"], ["proprietario", "Gestita dal proprietario"]].map(([val, label]) => (
                  <label key={val} data-testid={`feed-mgmt-${val}`} className={`flex items-center gap-2.5 rounded-lg border p-2.5 cursor-pointer ${mgmtEditor.managed_by === val ? "border-[#1B3B2B] bg-[#F7F3EC]" : "border-[#E5DEC9]"}`}>
                    <input type="radio" name="feed-mgmt-managed-by" checked={mgmtEditor.managed_by === val} onChange={() => setMgmtEditor({ ...mgmtEditor, managed_by: val })} className="h-4 w-4 accent-[#1B3B2B]" />
                    <span className="text-sm font-medium text-[#1B3B2B]">{label}</span>
                  </label>
                ))}
              </div>
              {mgmtEditor.managed_by === "proprietario" && (
                <div className="space-y-2 pl-1 border-l-2 border-[#EFE8D9] ml-1">
                  <span className="font-mono-label text-[10px] uppercase text-[#8C532B] pl-2">Ambito</span>
                  <div className="space-y-1.5 pl-2">
                    {[["all", "Tutte le fasce"], ["some", "Solo alcune fasce"]].map(([val, label]) => (
                      <label key={val} data-testid={`feed-mgmt-scope-${val}`} className="flex items-center gap-2.5 cursor-pointer">
                        <input type="radio" name="feed-mgmt-scope" checked={mgmtEditor.scope === val} onChange={() => setMgmtEditor({ ...mgmtEditor, scope: val })} className="h-4 w-4 accent-[#1B3B2B]" />
                        <span className="text-sm text-[#3C463F]">{label}</span>
                      </label>
                    ))}
                  </div>
                  {mgmtEditor.scope === "some" && (
                    <div className="grid grid-cols-2 gap-2 pl-2 pt-1">
                      {activeSlots.map((s) => {
                        const on = mgmtEditor.owner_slots.includes(s.id);
                        return (
                          <label key={s.id} data-testid={`feed-mgmt-slot-${s.id}`} className={`flex items-center gap-2 rounded-md border px-2.5 py-2 cursor-pointer ${on ? "border-[#1B3B2B] bg-[#F7F3EC]" : "border-[#E5DEC9]"}`}>
                            <input type="checkbox" checked={on} onChange={() => toggleOwnerSlot(s.id)} className="h-4 w-4 rounded border-[#C9BDA0] accent-[#1B3B2B]" />
                            <span className="text-sm text-[#1B3B2B] truncate">{s.name}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              <div><Label>Nota (facoltativa)</Label><Textarea className="mt-1.5" data-testid="feed-mgmt-note" value={mgmtEditor.note} onChange={(e) => setMgmtEditor({ ...mgmtEditor, note: e.target.value })} placeholder="Es. Il proprietario prepara autonomamente le razioni" /></div>
              <DialogFooter><Button type="button" variant="outline" onClick={() => setMgmtEditor(null)}>Annulla</Button><Button type="button" disabled={mgmtSaving} data-testid="feed-mgmt-save-btn" onClick={saveMgmt} className="bg-[#1B3B2B] hover:bg-[#142E1F]">{mgmtSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Salva</Button></DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {ration === undefined ? <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-[#1B3B2B]" /></div>
        : ration === null ? (
          <div className="bg-white rounded-xl border border-[#E5DEC9] py-14 text-center space-y-3" data-testid="profenda-empty">
            <p className="text-[#5F6B64]">Nessuna profenda configurata per questo cavallo.</p>
            {canManage && <Button data-testid="profenda-configure-btn" onClick={() => openEditor("new")} className="bg-[#1B3B2B] hover:bg-[#142E1F]"><Plus className="h-4 w-4 mr-2" /> Configura profenda</Button>}
          </div>
        ) : (
          <div className="space-y-4" data-testid="profenda-current">
            <div className="hidden sm:block bg-white rounded-xl border border-[#E5DEC9] overflow-hidden" data-testid="profenda-grid">
              <div className="grid text-sm" style={{ gridTemplateColumns: `minmax(90px,auto) repeat(${usedCats.length}, minmax(0,1fr))` }}>
                <div className="bg-[#F7F3EC] px-3 py-2 font-mono-label text-[10px] uppercase text-[#8C532B]">Fascia</div>
                {usedCats.map((c) => <div key={c} className="bg-[#F7F3EC] px-3 py-2 font-mono-label text-[10px] uppercase text-[#8C532B] border-l border-[#EFE8D9]">{c}</div>)}
                {groups.map((g) => (
                  <div key={g.slot.id} className="contents">
                    <div className="px-3 py-2.5 font-semibold text-[#1B3B2B] border-t border-[#F0EAE1]">{g.slot.name}</div>
                    {usedCats.map((c) => { const t = cellText(g.lines, c); return <div key={c} className="px-3 py-2.5 border-t border-l border-[#F0EAE1] text-[#3C463F]">{t || <span className="text-[#C9BDA0]">—</span>}</div>; })}
                  </div>
                ))}
              </div>
            </div>
            <div className="sm:hidden space-y-3">
              {groups.map((g) => (
                <div key={g.slot.id} className="bg-white rounded-xl border border-[#E5DEC9] overflow-hidden">
                  <div className="px-4 py-2 bg-[#F7F3EC] font-mono-label text-[11px] uppercase text-[#8C532B]">{g.slot.name}</div>
                  <div className="divide-y divide-[#F0EAE1]">
                    {usedCats.map((c) => { const t = cellText(g.lines, c); return (<div key={c} className="px-4 py-2 flex justify-between gap-3 text-sm"><span className="font-mono-label text-[10px] uppercase text-[#8C532B] pt-0.5">{c}</span><span className="text-right text-[#3C463F]">{t || <span className="text-[#C9BDA0]">—</span>}</span></div>); })}
                  </div>
                </div>
              ))}
            </div>
            {ration.note && <p className="text-sm text-[#5F6B64] bg-[#FCFAF6] rounded-lg p-3 border border-[#F0EAE1]">{ration.note}</p>}
          </div>
        )}

      <Dialog open={!!editor} onOpenChange={(o) => !o && setEditor(null)}>
        <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl" data-testid="profenda-editor">
          <DialogHeader><DialogTitle className="font-display">{editor?.mode === "correct" ? "Correggi profenda" : (ration ? "Modifica profenda (nuova versione)" : "Configura profenda")}</DialogTitle></DialogHeader>
          {editor && (
            <div className="space-y-4">
              {editor.mode !== "correct" ? <p className="text-xs text-[#8C532B] bg-[#F7F3EC] rounded-lg p-2.5">La modifica crea una <strong>nuova versione</strong> valida dalla data scelta; la precedente resta nello storico.</p>
                : <p className="text-xs text-[#8C6D4F] bg-[#FBF6EE] rounded-lg p-2.5">La correzione aggiorna la versione corrente senza creare una nuova versione storica.</p>}
              <div className="max-w-[200px]"><Label>Valida dal</Label><Input type="date" className="mt-1.5" data-testid="profenda-valid-from-input" value={editor.valid_from} onChange={(e) => setEditor({ ...editor, valid_from: e.target.value })} /></div>
              <RationGroupsEditor groups={editor.groups} onChange={(g) => setEditor({ ...editor, groups: g })} activeSlots={activeSlots} activeItems={activeItems} itemsMap={itemsMap} catOrder={catOrder} allowEmpty />
              <div><Label>Note generali</Label><Textarea className="mt-1.5" data-testid="profenda-note" value={editor.note} onChange={(e) => setEditor({ ...editor, note: e.target.value })} /></div>
              <DialogFooter><Button type="button" variant="outline" onClick={() => setEditor(null)}>Annulla</Button><Button type="button" disabled={saving} data-testid="profenda-save-btn" onClick={save} className="bg-[#1B3B2B] hover:bg-[#142E1F]">{saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Salva</Button></DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={delOpen} onOpenChange={(o) => !o && setDelOpen(false)}>
        <DialogContent data-testid="profenda-delete-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2 text-red-700"><AlertTriangle className="h-5 w-5" /> Elimina profenda</DialogTitle></DialogHeader>
          <p className="text-sm text-[#3C463F]">Stai per <strong>eliminare definitivamente</strong> la profenda corrente di <strong>{horse?.display_name}</strong>. Questa è un'eliminazione distruttiva: non crea una nuova versione. Il cavallo tornerà allo stato «Nessuna profenda configurata».</p>
          <p className="text-xs text-[#8C532B]">Eventuali versioni precedenti restano consultabili nello Storico e non verranno rese di nuovo correnti automaticamente.</p>
          <DialogFooter><Button type="button" variant="outline" data-testid="profenda-delete-cancel" onClick={() => setDelOpen(false)}>Annulla</Button><Button type="button" disabled={deleting} data-testid="profenda-delete-confirm" onClick={doDelete} className="bg-red-600 hover:bg-red-700">{deleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Elimina profenda</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={histOpen} onOpenChange={setHistOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="profenda-history">
          <DialogHeader><DialogTitle className="font-display">Storico profende</DialogTitle></DialogHeader>
          {!history ? <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#1B3B2B]" /></div>
            : history.length === 0 ? <p className="text-center text-[#5F6B64] py-8">Nessuno storico.</p>
            : (<div className="space-y-3">{history.map((v) => {
                const vg = groupRationBySlot(v.lines, activeSlots, itemsMap, catOrder);
                return (
                <div key={v.id} data-testid={`profenda-version-${v.id}`} className={`rounded-xl border p-3 ${v.status === "current" ? "border-[#1B3B2B] bg-[#F7F3EC]" : "border-[#E5DEC9] bg-white"}`}>
                  <div className="flex items-center justify-between mb-2"><p className="text-sm font-semibold text-[#1B3B2B]">{fmtDateIT(v.valid_from)} → {v.valid_to ? fmtDateIT(v.valid_to) : "in corso"}</p>{v.status === "current" && <span className="text-[10px] font-mono-label uppercase rounded-full px-2 py-0.5 bg-[#1B3B2B] text-white" data-testid={`profenda-version-current-${v.id}`}>Corrente</span>}</div>
                  {vg.length ? <div className="space-y-1.5">{vg.map((g, i) => (<div key={i}><span className="font-mono-label text-[10px] uppercase text-[#8C532B]">{g.slot.name}</span><SlotBlocks slotLines={g.lines} itemsMap={itemsMap} catOrder={catOrder} variant="inline" /></div>))}</div>
                    : <p className="text-xs text-[#9AA39C] italic">Nessun alimento</p>}
                </div>
              ); })}</div>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
